import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import type { Mesh } from 'three';
import type { Graph3DNodeRendererProps } from '../../core/renderer.types';
import { createEllipseShape, getNodeDataString } from './nodeShapeUtils';
import { NodeTextHitPlane } from './NodeTextHitPlane';

const HTML_CONTENT_Z_INDEX_RANGE: [number, number] = [160, 0];

export function ProcessNode3D({
  node,
  runtime,
  theme,
  hovered,
  selected,
  focused,
  onHover,
  onSelect,
  onFocus,
}: Graph3DNodeRendererProps) {
  const status = runtime?.status ?? 'idle';
  const active =
    runtime?.active || runtime?.highlighted || hovered || selected || focused;
  const themeColor = getNodeDataString(
    node.data,
    'themeColor',
    theme.runtime[status] ?? theme.node.border
  );
  const softBg = getNodeDataString(node.data, 'themeSoftBg', '#f3f4f6');
  const isRoot = Boolean(
    node.data &&
      typeof node.data === 'object' &&
      (node.data as Record<string, unknown>).isRoot
  );
  const isLeaf = Boolean(
    node.data &&
      typeof node.data === 'object' &&
      (node.data as Record<string, unknown>).isLeaf
  );
  const showRunningIndicator = status === 'running' && (node.depth ?? 0) > 1;
  const rootFaceFill = active ? '#314158' : '#d8e0ea';
  const rootSideFill = active ? '#22324a' : '#b8c3d1';
  const fill = isRoot
    ? rootFaceFill
    : !active
      ? isLeaf
        ? '#f8fafc'
        : '#ffffff'
      : isLeaf
        ? softBg
        : themeColor;
  const border = isRoot
    ? active
      ? '#314158'
      : '#94a3b8'
    : !active
      ? '#cbd5e1'
      : themeColor;
  const textColor = isRoot
    ? active
      ? '#ffffff'
      : '#1e293b'
    : !active
      ? '#9ca3af'
      : !isLeaf
        ? '#ffffff'
        : themeColor;
  const opacity = runtime?.hidden ? 0.16 : 1;
  const textFontSize = isRoot ? 15 : 13;
  const textLineHeight = isRoot ? '19px' : '17px';
  const shape = useMemo(
    () => createEllipseShape(node.resolvedSize.width, node.resolvedSize.height),
    [node.resolvedSize.height, node.resolvedSize.width]
  );
  const extrudeSettings = useMemo(
    () => ({
      depth: node.resolvedSize.depth,
      bevelEnabled: false,
      curveSegments: 48,
      steps: 1,
    }),
    [node.resolvedSize.depth]
  );
  const pulseRef = useRef<Mesh>(null);
  const orbitRef = useRef<Mesh>(null);

  useFrame(({ clock }) => {
    if (!showRunningIndicator) return;
    const elapsed = clock.getElapsedTime();
    const scale = 1 + Math.sin(elapsed * 4.2) * 0.035;
    if (pulseRef.current) {
      pulseRef.current.scale.set(
        (node.resolvedSize.width / 2 + 8) * scale,
        (node.resolvedSize.height / 2 + 8) * scale,
        1
      );
    }
    if (orbitRef.current) {
      orbitRef.current.position.set(
        Math.cos(elapsed * 3.4) * (node.resolvedSize.width / 2 + 7),
        -Math.sin(elapsed * 3.4) * (node.resolvedSize.height / 2 + 7),
        node.resolvedSize.depth / 2 + 7
      );
    }
  });

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSelect(node.id);
    onFocus(node.id);
  };

  return (
    <group
      position={[
        node.resolvedPosition.x,
        node.resolvedPosition.y,
        node.resolvedPosition.z,
      ]}
      onClick={handleClick}
      onPointerEnter={event => {
        event.stopPropagation();
        onHover(node.id);
      }}
      onPointerLeave={event => {
        event.stopPropagation();
        onHover(null);
      }}
    >
      {showRunningIndicator && (
        <mesh ref={pulseRef} position={[0, 0, node.resolvedSize.depth / 2 + 3]}>
          <torusGeometry args={[1, 0.02, 8, 96]} />
          <meshBasicMaterial color={themeColor} transparent opacity={0.5} />
        </mesh>
      )}
      <mesh position={[0, 0, -node.resolvedSize.depth / 2]}>
        <extrudeGeometry args={[shape, extrudeSettings]} />
        {isRoot ? (
          <>
            <meshStandardMaterial
              attach="material-0"
              color={rootFaceFill}
              emissive={border}
              emissiveIntensity={active ? 0.08 : 0.02}
              roughness={active ? 0.36 : 0.44}
              metalness={0.02}
              transparent
              opacity={opacity}
            />
            <meshStandardMaterial
              attach="material-1"
              color={rootSideFill}
              emissive={rootSideFill}
              emissiveIntensity={active ? 0.03 : 0.01}
              roughness={active ? 0.5 : 0.56}
              metalness={0.01}
              transparent
              opacity={opacity}
            />
          </>
        ) : (
          <meshStandardMaterial
            color={fill}
            emissive={border}
            emissiveIntensity={active ? 0.3 : 0.08}
            roughness={0.42}
            metalness={0.02}
            transparent
            opacity={opacity}
          />
        )}
      </mesh>
      <mesh
        position={[0, 0, node.resolvedSize.depth / 2 + 0.8]}
        scale={[node.resolvedSize.width / 2, node.resolvedSize.height / 2, 1]}
      >
        <torusGeometry args={[1, 0.025, 8, 96]} />
        <meshBasicMaterial
          color={border}
          transparent
          opacity={active ? 0.96 : isRoot ? 0.42 : 0.7}
        />
      </mesh>
      {showRunningIndicator && (
        <mesh ref={orbitRef}>
          <sphereGeometry args={[4.8, 18, 12]} />
          <meshBasicMaterial color="#10b981" />
        </mesh>
      )}
      <NodeTextHitPlane
        width={node.resolvedSize.width}
        height={node.resolvedSize.height}
        z={node.resolvedSize.depth / 2 + 8}
      />
      <Html
        center
        transform
        distanceFactor={400}
        zIndexRange={HTML_CONTENT_Z_INDEX_RANGE}
        position={[0, 0, node.resolvedSize.depth / 2 + 8]}
      >
        <div
          style={{
            width: node.resolvedSize.width,
            color: textColor,
            fontFamily:
              'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
            fontSize: textFontSize,
            fontWeight: 700,
            lineHeight: textLineHeight,
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
            overflow: 'visible',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            textShadow:
              active && (isRoot || !isLeaf)
                ? '0 1px 1px rgba(0,0,0,.18)'
                : 'none',
          }}
        >
          {node.label}
        </div>
      </Html>
    </group>
  );
}
